import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Card, CardContent, CardHeader } from 'components/ui/card'
import {
	Table,
	TableHeader,
	TableBody,
	TableHead,
	TableRow,
	TableCell,
} from 'components/ui/table'
import { Building2 } from 'lucide-react'
import { fetchSites } from 'slices/evaluationSiteSlice'
import { fetchFinancialsComparison } from 'slices/siteFinancialSlice'
import FinancialSiteCard from './FinancialSiteCard'

interface LineItem {
	amount?: number
}

const MODEL_KEYS: Array<'acquisition' | 'operating' | 'roi'> = ['acquisition', 'operating', 'roi']

const formatCurrency = (n: number) =>
	n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

const sumLineItems = (items: LineItem[] | undefined): number =>
	Array.isArray(items) ? items.reduce((s, i) => s + (Number(i.amount) || 0), 0) : 0

const EvaluationFinancials = () => {
	const { id: evalId = '' } = useParams<{ id: string }>()
	const dispatch = useDispatch()

	const { entities: siteEntities, byEvaluation } = useSelector((state: any) => state.evaluationSites)
	const { comparisonByEvaluation } = useSelector((state: any) => state.siteFinancials)

	const sites = useMemo(() => {
		const ids: string[] = byEvaluation[evalId] || []
		return ids.map((id) => siteEntities[id]).filter(Boolean)
	}, [byEvaluation, siteEntities, evalId])

	useEffect(() => {
		// @ts-ignore
		dispatch(fetchSites(evalId))
		// @ts-ignore
		dispatch(fetchFinancialsComparison(evalId))
	}, [dispatch, evalId])

	const comparison = comparisonByEvaluation[evalId] || []

	if (sites.length === 0) {
		return (
			<div className="text-center py-12">
				<Building2 className="mx-auto h-12 w-12 text-gray-300 mb-3" />
				<p className="text-sm text-gray-500">
					No sites yet. Add sites in the Sites tab before modeling financials.
				</p>
			</div>
		)
	}

	return (
		<div className="space-y-5">
			<div>
				<h3 className="text-lg font-semibold text-gray-900">Financial Modeling</h3>
				<p className="text-sm text-gray-500">
					Per-site Acquisition, Operating, and ROI projections — scroll horizontally to compare.
				</p>
			</div>

			<div className="flex gap-4 overflow-x-auto pb-4">
				{sites.map((site: any, idx: number) => (
					<FinancialSiteCard
						key={site._id}
						evalId={evalId}
						site={site}
						letter={String.fromCharCode(65 + idx)}
					/>
				))}
			</div>

			{comparison.length > 1 && (
				<Card>
					<CardHeader className="pb-3">
						<h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
							Cross-site Comparison
						</h4>
						<p className="text-xs text-gray-500 mt-1">
							Totals for each model across sites.
						</p>
					</CardHeader>
					<CardContent>
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Site</TableHead>
									<TableHead className="text-right">Acquisition</TableHead>
									<TableHead className="text-right">Operating</TableHead>
									<TableHead className="text-right">ROI</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{comparison.map((row: any) => (
									<TableRow key={row.siteId}>
										<TableCell className="font-medium text-gray-900">{row.siteName}</TableCell>
										{MODEL_KEYS.map((k) => {
											const model = row.models?.[k]
											return (
												<TableCell key={k} className="text-right text-gray-700">
													{model ? (
														formatCurrency(sumLineItems(model.lineItems))
													) : (
														<span className="text-gray-300">—</span>
													)}
												</TableCell>
											)
										})}
									</TableRow>
								))}
							</TableBody>
						</Table>
					</CardContent>
				</Card>
			)}
		</div>
	)
}

export default EvaluationFinancials
